import {FC, useState, useRef, useCallback} from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button,
    Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
    Paper, Typography, Alert, Box, Chip, Stack, LinearProgress,
} from '@mui/material';
import {Upload} from '@mui/icons-material';

export interface CsvColumnDef {
    key: string;       // CSV header name
    label: string;     // Display label
    required?: boolean;
    readonly?: boolean;
}

interface CsvUploadDialogProps {
    open: boolean;
    onClose: () => void;
    title: string;
    columns: CsvColumnDef[];
    onImport: (rows: Record<string, string>[]) => Promise<{inserted?: number; updated?: number; skipped?: number; total?: number}>;
    isLoading?: boolean;
}

function parseCsvLine(line: string): string[] {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
        const char = line[i];
        if (inQuotes) {
            if (char === '"') {
                if (i + 1 < line.length && line[i + 1] === '"') {
                    current += '"';
                    i++;
                } else {
                    inQuotes = false;
                }
            } else {
                current += char;
            }
        } else {
            if (char === '"') {
                inQuotes = true;
            } else if (char === ',') {
                result.push(current.trim());
                current = '';
            } else {
                current += char;
            }
        }
    }
    result.push(current.trim());
    return result;
}

function parseCsv(text: string): {headers: string[]; rows: Record<string, string>[]} {
    const lines = text.split(/\r?\n/).filter(l => l.trim().length > 0);
    if (lines.length === 0) return {headers: [], rows: []};

    const headers = parseCsvLine(lines[0]);
    const rows = lines.slice(1).map(line => {
        const values = parseCsvLine(line);
        const row: Record<string, string> = {};
        headers.forEach((h, i) => {
            row[h] = values[i] ?? '';
        });
        return row;
    });

    return {headers, rows};
}

export const CsvUploadDialog: FC<CsvUploadDialogProps> = ({
    open, onClose, title, columns, onImport, isLoading,
}) => {
    const fileRef = useRef<HTMLInputElement>(null);
    const [parsed, setParsed] = useState<Record<string, string>[] | null>(null);
    const [headers, setHeaders] = useState<string[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [result, setResult] = useState<{inserted?: number; updated?: number; skipped?: number; total?: number} | null>(null);

    const reset = useCallback(() => {
        setParsed(null);
        setHeaders([]);
        setError(null);
        setResult(null);
        if (fileRef.current) fileRef.current.value = '';
    }, []);

    const handleClose = () => {
        reset();
        onClose();
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setError(null);
        setResult(null);
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (ev) => {
            try {
                const text = ev.target?.result as string;
                const {headers: h, rows} = parseCsv(text);
                if (rows.length === 0) {
                    setError('CSV에 데이터가 없습니다.');
                    return;
                }
                // Validate required columns exist in headers
                const requiredCols = columns.filter(c => c.required).map(c => c.key);
                const missing = requiredCols.filter(r => !h.includes(r));
                if (missing.length > 0) {
                    setError(`필수 컬럼 누락: ${missing.join(', ')}`);
                    return;
                }
                setHeaders(h);
                setParsed(rows);
            } catch {
                setError('CSV 파싱 실패');
            }
        };
        reader.readAsText(file);
    };

    const handleImport = async () => {
        if (!parsed) return;
        try {
            setError(null);
            const res = await onImport(parsed);
            setResult(res);
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : '임포트 실패');
        }
    };

    const displayCols = headers.length > 0
        ? headers.filter(h => columns.some(c => c.key === h))
        : columns.filter(c => !c.readonly).map(c => c.key);

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{mt: 1}}>
                    {/* Column info */}
                    <Box>
                        <Typography variant="caption" color="text.secondary">
                            CSV 헤더 형식:
                        </Typography>
                        <Box sx={{mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5}}>
                            {columns.map(col => (
                                <Chip
                                    key={col.key}
                                    label={col.key}
                                    size="small"
                                    color={col.required ? 'primary' : col.readonly ? 'default' : 'info'}
                                    variant={col.readonly ? 'outlined' : 'filled'}
                                    title={`${col.label}${col.required ? ' (필수)' : ''}${col.readonly ? ' (읽기전용)' : ''}`}
                                />
                            ))}
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{mt: 0.5, display: 'block'}}>
                            <Chip label="필수" size="small" color="primary" sx={{mr: 0.5, height: 18, fontSize: '0.7rem'}}/>
                            <Chip label="선택" size="small" color="info" sx={{mr: 0.5, height: 18, fontSize: '0.7rem'}}/>
                            <Chip label="읽기전용" size="small" variant="outlined" sx={{height: 18, fontSize: '0.7rem'}}/>
                        </Typography>
                    </Box>

                    {/* File input */}
                    <Button variant="outlined" component="label" startIcon={<Upload/>}>
                        CSV 파일 선택
                        <input ref={fileRef} type="file" accept=".csv" hidden onChange={handleFileChange}/>
                    </Button>

                    {isLoading && <LinearProgress/>}

                    {error && <Alert severity="error">{error}</Alert>}

                    {result && (
                        <Alert severity="success">
                            총 {result.total ?? (parsed?.length ?? 0)}건 처리 완료
                            {result.inserted !== undefined && ` | 추가: ${result.inserted}`}
                            {result.updated !== undefined && ` | 수정: ${result.updated}`}
                            {result.skipped !== undefined && ` | 건너뜀: ${result.skipped}`}
                        </Alert>
                    )}

                    {/* Preview table */}
                    {parsed && !result && (
                        <>
                            <Typography variant="body2" color="text.secondary">
                                미리보기 ({Math.min(parsed.length, 50)} / {parsed.length}건)
                            </Typography>
                            <TableContainer component={Paper} sx={{maxHeight: 400}}>
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{fontWeight: 'bold', whiteSpace: 'nowrap'}}>#</TableCell>
                                            {displayCols.map(h => {
                                                const col = columns.find(c => c.key === h);
                                                return (
                                                    <TableCell key={h} sx={{fontWeight: 'bold', whiteSpace: 'nowrap'}}>
                                                        {col?.label ?? h}
                                                        {col?.required && <Chip label="필수" size="small" color="primary" sx={{ml: 0.5, height: 16, fontSize: '0.6rem'}}/>}
                                                    </TableCell>
                                                );
                                            })}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {parsed.slice(0, 50).map((row, i) => (
                                            <TableRow key={i}>
                                                <TableCell>{i + 1}</TableCell>
                                                {displayCols.map(h => (
                                                    <TableCell key={h} sx={{maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                                                        {row[h] || '-'}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>닫기</Button>
                {parsed && !result && (
                    <Button variant="contained" onClick={handleImport} disabled={isLoading}>
                        {parsed.length}건 임포트
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

// Utility: generate CSV string from data
export function generateCsv(headers: string[], rows: Record<string, unknown>[]): string {
    const escapeField = (val: unknown): string => {
        const str = val == null ? '' : String(val);
        if (str.includes(',') || str.includes('"') || str.includes('\n')) {
            return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
    };

    const lines = [
        headers.join(','),
        ...rows.map(row => headers.map(h => escapeField(row[h])).join(',')),
    ];
    return lines.join('\n');
}

// Utility: trigger CSV download
export function downloadCsv(filename: string, csvContent: string) {
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], {type: 'text/csv;charset=utf-8;'});
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}
